import React, { useState, useMemo } from 'react';
import { Cobranca } from '../../types/financeiro';
import { Search, Receipt, ExternalLink, AlertCircle, CheckCircle, Clock } from 'lucide-react';

interface FinanceiroCobrancasSectionProps {
  cobrancas: Cobranca[];
  loading: boolean;
}

export function FinanceiroCobrancasSection({ cobrancas, loading }: FinanceiroCobrancasSectionProps) {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredCobrancas = useMemo(() => {
    if (!searchTerm.trim()) return cobrancas;
    const term = searchTerm.toLowerCase();
    return cobrancas.filter((cob) => {
      const matchId = String(cob.id).includes(term);
      const matchConta = String(cob.conta_id).includes(term);
      const matchCode = (cob.codigo || '').toLowerCase().includes(term);
      const matchLink = (cob.link || '').toLowerCase().includes(term);
      return matchId || matchConta || matchCode || matchLink;
    });
  }, [cobrancas, searchTerm]);

  return (
    <div className="space-y-4">
      {/* Search Bar for Cobranças */}
      <div className="bg-white p-3.5 rounded-2xl border border-slate-200 shadow-2xs flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar por ID, Conta, Código ou Link..."
            className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
          />
        </div>
        <div className="text-xs font-semibold text-slate-500">
          Total: <span className="text-slate-800 font-bold">{filteredCobrancas.length}</span> cobrança(s)
        </div>
      </div>

      {/* Table */}
      <div className="bg-white shadow-xs rounded-2xl overflow-hidden border border-slate-200">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-5 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">ID</th>
                <th className="px-5 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">Conta Vinculada</th>
                <th className="px-5 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">Código / Link de Pagamento</th>
                <th className="px-5 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">Data Pagamento</th>
                <th className="px-5 py-3 text-right text-xs font-bold text-slate-600 uppercase tracking-wider">Valor Total</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-100 text-xs">
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-6 py-10 text-center text-slate-500">
                    Carregando cobranças...
                  </td>
                </tr>
              ) : filteredCobrancas.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-10 text-center text-slate-500">
                    <Receipt className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                    Nenhuma cobrança encontrada.
                  </td>
                </tr>
              ) : (
                filteredCobrancas.map((cob) => (
                  <tr key={`cobranca-${cob.id}`} className="hover:bg-slate-50/70 transition-colors">
                    <td className="px-5 py-3.5 font-bold text-slate-900">#{cob.id}</td>
                    <td className="px-5 py-3.5 text-slate-700">
                      <span className="bg-slate-100 px-2 py-0.5 rounded text-[11px] font-semibold">
                        Conta #{cob.conta_id}
                      </span>
                    </td>
                    <td className="px-5 py-3.5 text-slate-600 max-w-xs truncate">
                      {cob.link ? (
                        <a
                          href={cob.link}
                          target="_blank"
                          rel="noreferrer"
                          className="text-blue-600 hover:underline inline-flex items-center gap-1"
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                          <span>{cob.link}</span>
                        </a>
                      ) : (
                        <span className="font-mono text-slate-500">{cob.codigo || '-'}</span>
                      )}
                    </td>
                    <td className="px-5 py-3.5 text-slate-600">
                      {cob.data_pagamento ? new Date(cob.data_pagamento).toLocaleDateString('pt-BR') : '-'}
                    </td>
                    <td className="px-5 py-3.5 text-right font-bold text-slate-900">
                      {cob.valor_total || '-'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
